import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { hasBackgroundProcessorFailureAtom, videoSDKAdapterAtom } from '../core/atoms';
import { useStoredAudioVideoSettings } from '../core/hooks/useStoredAudioVideoSettings';
import { localVideoContextAtom } from '../core/room/participant/media/atoms';
import { BackgroundSettings, PublishMediaOptions, SetCameraResult } from '../core/types';

export function useStartVideo() {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const setLocalVideoContext = useSetAtom(localVideoContextAtom);
  const getLocalVideoContext = useAtomCallbackMemoized((get) => get(localVideoContextAtom), []);
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();

  return useCallback(
    async (options: PublishMediaOptions<unknown, unknown> = {}): Promise<boolean> => {
      return await getAdapter().startVideo(
        getLocalVideoContext,
        setLocalVideoContext,
        setStoredAudioVideoSettings,
        options,
      );
    },
    [getAdapter, getLocalVideoContext, setLocalVideoContext, setStoredAudioVideoSettings],
  );
}

export function useStopVideo() {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const setLocalVideoContext = useSetAtom(localVideoContextAtom);
  const getLocalVideoContext = useAtomCallbackMemoized((get) => get(localVideoContextAtom), []);

  return useCallback(
    async (shouldReleaseContext?: boolean): Promise<boolean> => {
      return await getAdapter().stopVideo(getLocalVideoContext, setLocalVideoContext, shouldReleaseContext);
    },
    [getAdapter, setLocalVideoContext, getLocalVideoContext],
  );
}

export function useSwitchCamera() {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const setLocalVideoContext = useSetAtom(localVideoContextAtom);
  const getLocalVideoContext = useAtomCallbackMemoized((get) => get(localVideoContextAtom), []);
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  const setHasBackgroundProcessorFailure = useSetAtom(hasBackgroundProcessorFailureAtom);

  return useCallback(
    async (cameraDeviceId: string): Promise<SetCameraResult> => {
      return await getAdapter().switchCamera(
        cameraDeviceId,
        getLocalVideoContext,
        setLocalVideoContext,
        setStoredAudioVideoSettings,
        setHasBackgroundProcessorFailure,
      );
    },
    [
      getAdapter,
      setLocalVideoContext,
      getLocalVideoContext,
      setStoredAudioVideoSettings,
      setHasBackgroundProcessorFailure,
    ],
  );
}

export function useSwitchBackground() {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const setLocalVideoContext = useSetAtom(localVideoContextAtom);
  const getLocalVideoContext = useAtomCallbackMemoized((get) => get(localVideoContextAtom), []);
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  const setHasBackgroundProcessorFailure = useSetAtom(hasBackgroundProcessorFailureAtom);

  return useCallback(
    async (backgroundSettings: BackgroundSettings, options?: { persistChanges?: boolean }): Promise<void> => {
      await getAdapter().switchBackground(
        backgroundSettings,
        getLocalVideoContext,
        setLocalVideoContext,
        setStoredAudioVideoSettings,
        setHasBackgroundProcessorFailure,
        options,
      );
    },
    [
      getAdapter,
      setLocalVideoContext,
      getLocalVideoContext,
      setStoredAudioVideoSettings,
      setHasBackgroundProcessorFailure,
    ],
  );
}
